import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import { useDispatch, useSelector } from 'react-redux';
import { Box, ButtonBase, Typography } from '@material-ui/core';
import clsx from 'clsx';
import AddIcon from '@material-ui/icons/Add';
import HomeOutlinedIcon from '@material-ui/icons/HomeOutlined';
import { RMSButton, RMSIconButton } from 'features/shared/components/buttons';
import PersonOutlineIcon from '@material-ui/icons/PersonOutline';
import {
  getPersonDetails,
  getPersonImages,
  getPeopleContactMethods,
  getPeopleAssociates,
  getPersonRecordDetails,
  getPeopleTags,
} from 'features/user/services/userService';
import { getPeopleVehicleRegistrations } from 'features/user/services/vehicleService';
import { getPeopleAddresses } from 'features/user/services/addressService';
import { getPeopleSops } from 'features/user/services/sopService';
import { getPeopleCredentials } from 'features/user/services/credentialService';
import { CONTACT_METHOD_TYPE } from 'features/user/constants/addPersonFormConstants';
import { CREDENTIAL_TYPE } from 'features/user/constants/personCredentialFormConstants';
import { RECORD_CATEGORY_KEYS } from 'features/user/constants/personRelatedRecordsTabConstants';
import {
  resolveDescription,
  calculateAge,
} from 'features/user/utility/personInfoSummaryCardHelper';
import useUserDictionaryOptions from 'features/user/hooks/useUserDictionaryOptions';
import DATE_FORMATS from 'features/shared/constants/dateFormats';
import { formatDate } from 'features/shared/utility/dateFormatter';
import { showReport, showWarrants } from 'reducers/PersonDialogsReducer';
import { parseRelatedRecordRows } from 'features/user/utility/personRelatedRecordsHelper';
import { PersonMenuItemKey } from 'features/user/constants/personMenuItemKeys';
import PrintIcon from '@material-ui/icons/Print';
import MergeTypeIcon from '@material-ui/icons/MergeType';
import { getPersonMergeColumns } from 'features/user/constants/personSearchColumns';
import PersonMerge from 'pages/PageRMSRecords/PeopleSearch/PersonMerge';
import {
  PERSON_HEADER_STATS,
  PERSON_HEADER_STAT_KEYS,
} from 'features/user/constants/personHeaderStats';
import { SLIDER_SETTINGS } from 'features/user/constants/personInfoSummaryCardFormConstants';
import {
  resolveDescription as resolveTagDescription,
  resolveIcon,
} from 'features/user/utility/personTagHelper';
import GavelIcon from '@material-ui/icons/Gavel';
import { findBulletinsData } from 'reducers/BulletinReducer';
import { searchWarrants } from 'reducers/SearchReducer';
import { executeApiAction, executeMultipleApiActions } from 'features/shared/utility/apiHelper';
import FormSpinner from 'components/FormSpinner';

const PersonHeader = ({
  personId,
  onMenuSelect,
  onAddTagClick,
  onNewWarrantClick,
  refreshKey = 0,
}) => {
  const dispatch = useDispatch();
  const canMergePerson = usePermissions(PERMISSION_RESOURCES.MERGE_PEOPLE, null) === true;
  const personPermissions = usePermissions(PERMISSION_RESOURCES.PERSON, null);
  const mergeColumns = getPersonMergeColumns(personPermissions.ViewDeleted);
  const [mergePerson, setMergePerson] = useState(null);
  const [mergeRefreshKey, setMergeRefreshKey] = useState(0);
  const { page, pageSize } = useSelector((state) => state.warrant.pagination);
  const warrantUpdate = useSelector((state) =>
    state.dataUpdate?.type === 'Warrant' ? state.dataUpdate : null
  );
  const userId = useSelector((state) => state.user?.userData?.user?.ptsUserID);
  const agencyId = useSelector((state) => state.user?.userAgency);
  const clearanceLevel = useSelector((state) => state.user?.userCurrentClearanceLevel?.Level);
  const { raceOptions, sexOptions, ethnicityOptions, tagTypeOptions } = useUserDictionaryOptions();
  const [person, setPerson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [warrantsLoading, setWarrantsLoading] = useState(false);
  const isLoading = loading || warrantsLoading;
  const [photos, setPhotos] = useState([]);
  const [address, setAddress] = useState('');
  const [tags, setTags] = useState([]);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [ssn, setSsn] = useState('');
  const [oln, setOln] = useState('');
  const [warrants, setWarrants] = useState([]);
  const [warrantRefreshKey, setWarrantRefreshKey] = useState(0);

  const [counts, setCounts] = useState({
    [PERSON_HEADER_STAT_KEYS.BOOKINGS]: 0,
    [PERSON_HEADER_STAT_KEYS.WARRANTS]: 0,
    [PERSON_HEADER_STAT_KEYS.INCIDENTS]: 0,
    [PERSON_HEADER_STAT_KEYS.ASSOCIATES]: 0,
    [PERSON_HEADER_STAT_KEYS.VEHICLES]: 0,
    [PERSON_HEADER_STAT_KEYS.SOPS]: 0,
    [PERSON_HEADER_STAT_KEYS.BULLETINS]: 0,
  });

  useEffect(() => {
    if (!personId) {
      setLoading(false);
      setPerson(null);
      setPhotos([]);
      setAddress('');
      setTags([]);
      setPhone('');
      setEmail('');
      setSsn('');
      setOln('');
      setWarrants([]);
      setCounts({
        [PERSON_HEADER_STAT_KEYS.BOOKINGS]: 0,
        [PERSON_HEADER_STAT_KEYS.WARRANTS]: 0,
        [PERSON_HEADER_STAT_KEYS.INCIDENTS]: 0,
        [PERSON_HEADER_STAT_KEYS.ASSOCIATES]: 0,
        [PERSON_HEADER_STAT_KEYS.VEHICLES]: 0,
        [PERSON_HEADER_STAT_KEYS.SOPS]: 0,
        [PERSON_HEADER_STAT_KEYS.BULLETINS]: 0,
      });
      return undefined;
    }

    let active = true;
    const loadHeader = async () => {
      // Defaults preserve partial results when an individual request fails.
      const headerData = {
        details: [],
        images: [],
        addresses: [],
        contacts: [],
        credentials: [],
        associates: [],
        vehicles: [],
        records: {},
        personTags: [],
        sops: [],
        bulletins: [],
        activeWarrants: [],
      };
      const actions = {
        details: () => getPersonDetails(personId),
        images: () => getPersonImages(personId),
        addresses: () => getPeopleAddresses(personId),
        contacts: () => getPeopleContactMethods(personId),
        credentials: () => getPeopleCredentials(personId),
        associates: () => getPeopleAssociates(personId),
        vehicles: () => getPeopleVehicleRegistrations(personId),
        records: () => getPersonRecordDetails(personId, true),
        personTags: () => getPeopleTags(personId),
        sops: () => getPeopleSops(personId),
        bulletins: () => findBulletinsData({ ptsPersonID: personId }),
        activeWarrants: () =>
          searchWarrants({ activeOnly: true, ptsPersonID: personId }, page, pageSize),
      };
      await executeMultipleApiActions({
        setLoading: (value) => {
          if (active) setLoading(value);
        },
        actions: Object.entries(actions).map(([key, action]) => ({
          action,
          onSuccess: (result) => {
            headerData[key] = result;
          },
        })),
      });
      if (!active) return;
      const {
        details: [details],
        images,
        addresses,
        contacts,
        credentials,
        associates,
        vehicles,
        records,
        personTags,
        sops,
        bulletins,
        activeWarrants,
      } = headerData;
      setWarrants(Array.isArray(activeWarrants) ? activeWarrants : []);
      setPerson(details || null);
      setTags((personTags || []).filter((tag) => !tag.IsDeleted));
      setPhotos((images || []).filter((item) => !item.IsMark));
      setAddress(addresses.find((item) => item.IsPrimary)?.FullAddressText || '');
      setPhone(
        contacts.find((item) => item.ContactMethod === CONTACT_METHOD_TYPE.PHONE)?.Info || ''
      );
      setEmail(
        contacts.find((item) => item.ContactMethod === CONTACT_METHOD_TYPE.EMAIL)?.Info || ''
      );
      setSsn(credentials.find((item) => item.Type === CREDENTIAL_TYPE.SSN)?.Number || '');
      setOln(credentials.find((item) => item.Type === CREDENTIAL_TYPE.OLN)?.Number || '');
      setCounts({
        [PERSON_HEADER_STAT_KEYS.BOOKINGS]: parseRelatedRecordRows(
          records?.[RECORD_CATEGORY_KEYS.BOOKINGS]
        ).length,
        [PERSON_HEADER_STAT_KEYS.WARRANTS]: Array.isArray(activeWarrants)
          ? activeWarrants.length
          : 0,
        [PERSON_HEADER_STAT_KEYS.INCIDENTS]:
          parseRelatedRecordRows(records?.[RECORD_CATEGORY_KEYS.CAD_INCIDENTS]).length +
          parseRelatedRecordRows(records?.[RECORD_CATEGORY_KEYS.RMS_INCIDENTS]).length,
        [PERSON_HEADER_STAT_KEYS.ASSOCIATES]: associates.length || 0,
        [PERSON_HEADER_STAT_KEYS.VEHICLES]: vehicles.length || 0,
        [PERSON_HEADER_STAT_KEYS.SOPS]: Array.isArray(sops) ? sops.length : 0,
        [PERSON_HEADER_STAT_KEYS.BULLETINS]: Array.isArray(bulletins) ? bulletins.length : 0,
      });
    };
    loadHeader();
    return () => {
      active = false;
    };
  }, [personId, refreshKey, page, pageSize, mergeRefreshKey]);

  const photoSliderSettings = { ...SLIDER_SETTINGS, dots: false, arrows: photos.length > 1 };

  const handleStatClick = (key, menuKey) => {
    if (key === PERSON_HEADER_STAT_KEYS.WARRANTS) {
      if (!(counts[PERSON_HEADER_STAT_KEYS.WARRANTS] > 0)) return;
      dispatch(showWarrants({ warrants }));
      return;
    }
    onMenuSelect?.(menuKey);
  };

  const printPersonHistory = () => {
    if (!personId) return;
    dispatch(
      showReport({
        id: 250,
        title: 'Person History Report',
        data: {
          ptsPersonID: personId,
          AgencyID: agencyId,
          ClearanceLevel: parseInt(clearanceLevel, 10) || '',
          userId,
        },
      })
    );
  };

  useEffect(() => {
    if (warrantUpdate) setWarrantRefreshKey((key) => key + 1);
  }, [warrantUpdate]);

  useEffect(() => {
    if (!personId || !warrantRefreshKey) {
      setWarrantsLoading(false);
      return undefined;
    }
    let active = true;
    executeApiAction({
      setLoading: (value) => {
        if (active) setWarrantsLoading(value);
      },
      action: () => searchWarrants({ activeOnly: true, ptsPersonID: personId }, page, pageSize),
      onSuccess: (rows) => {
        if (!active) return;
        const activeWarrants = Array.isArray(rows) ? rows : [];
        setWarrants(activeWarrants);
        setCounts((previous) => ({
          ...previous,
          [PERSON_HEADER_STAT_KEYS.WARRANTS]: activeWarrants.length,
        }));
      },
    });
    return () => {
      active = false;
    };
  }, [personId, warrantRefreshKey, page, pageSize]);

  return (
    <Box className="pd-header" aria-busy={isLoading}>
      {isLoading && <FormSpinner />}
      <Box className="pd-header__profile pd-card">
        <Box className="pd-header__identity">
          <Box className="pd-header__photo">
            {photos.length > 0 ? (
              <Slider {...photoSliderSettings} className="pd-header__photo-slider">
                {photos.map((image) => (
                  <div key={image.ptsImageID}>
                    {image.Image ? (
                      <img
                        src={`data:image/jpeg;base64,${image.Image}`}
                        alt={image.FileName || person?.FullName || 'Person'}
                      />
                    ) : (
                      <PersonOutlineIcon />
                    )}
                  </div>
                ))}
              </Slider>
            ) : (
              <PersonOutlineIcon />
            )}
          </Box>
          <Box className="pd-header__details">
            <Typography className="pd-header__name">
              {person
                ? `${person.PersonID || person.ptsPersonID || ''} | ${person.FullName || ''}`
                : 'Person details'}
            </Typography>
            <Box className="pd-header__fields">
              <span>
                DOB:{' '}
                <b>
                  {person?.Born ? formatDate(person.Born, DATE_FORMATS.MM_DD_YYYY_NUMERIC) : '—'}
                </b>
              </span>
              <span>
                Age: <b>{calculateAge(person?.Born) || '—'}</b>
              </span>
              <span>
                Race: <b>{resolveDescription(raceOptions, person?.Race) || '—'}</b>
              </span>
              <span>
                Sex: <b>{resolveDescription(sexOptions, person?.Sex) || '—'}</b>
              </span>
              <span>
                Ethnicity: <b>{resolveDescription(ethnicityOptions, person?.Ethnicity) || '—'}</b>
              </span>
            </Box>
            <Box className="pd-header__fields">
              <span>
                SSN:{' '}
                {ssn ? (
                  <ButtonBase
                    className="pd-header__credential-link"
                    aria-label={`Open Credential tab for SSN ${ssn}`}
                    onClick={() => onMenuSelect?.(PersonMenuItemKey.CREDENTIAL)}>
                    <b>{ssn}</b>
                  </ButtonBase>
                ) : (
                  <b>—</b>
                )}
              </span>
              <span>
                OLN:{' '}
                {oln ? (
                  <ButtonBase
                    className="pd-header__credential-link"
                    aria-label={`Open Credential tab for OLN ${oln}`}
                    onClick={() => onMenuSelect?.(PersonMenuItemKey.CREDENTIAL)}>
                    <b>{oln}</b>
                  </ButtonBase>
                ) : (
                  <b>—</b>
                )}
              </span>
            </Box>
            <Box className="pd-header__fields">
              <span>
                Email: <b>{email || '—'}</b>
              </span>
              <span>
                Phone: <b>{phone || '—'}</b>
              </span>
            </Box>
            {personId && onNewWarrantClick && (
              <Box className="pd-header__quick-actions">
                {onNewWarrantClick && (
                  <RMSButton
                    label="New Warrant"
                    prefixIcon={<GavelIcon fontSize="small" />}
                    variant="outlined"
                    size="small"
                    className="pd-header__quick-action-btn"
                    onClick={onNewWarrantClick}
                  />
                )}
              </Box>
            )}
            {address && (
              <ButtonBase
                className="pd-header__address"
                aria-label={`Open Addresses: ${address}`}
                onClick={() => onMenuSelect?.(PersonMenuItemKey.ADDRESSES)}>
                <HomeOutlinedIcon fontSize="small" />
                <span>{address}</span>
              </ButtonBase>
            )}
          </Box>
          <Box className="pd-header__stats">
            {PERSON_HEADER_STATS.map(({ key, label, menuKey, highlight }) => (
              <Box
                className={clsx('pd-header__stat', {
                  'pd-header__stat--clickable': menuKey,
                  [`pd-header__stat--${highlight}`]: highlight && counts[key] > 0,
                })}
                key={key}
                role={menuKey ? 'button' : undefined}
                tabIndex={menuKey ? 0 : undefined}
                aria-label={
                  menuKey
                    ? `Open ${label}${
                        key === PERSON_HEADER_STAT_KEYS.WARRANTS ? ' dialog' : ' tab'
                      }`
                    : undefined
                }
                onClick={menuKey ? () => handleStatClick(key, menuKey) : undefined}
                onKeyDown={
                  menuKey
                    ? (event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleStatClick(key, menuKey);
                        }
                      }
                    : undefined
                }>
                <span>{label}</span>
                <b>{counts[key]}</b>
              </Box>
            ))}
          </Box>
        </Box>
        <Box className="pd-header__tags">
          <div className="pd-header__tag-list">
            {tags.map((tag) => {
              const tagType = tagTypeOptions.find((option) => option.Code === tag.Type);
              const TagIcon = resolveIcon(tagType?.Icon);
              const description = [resolveTagDescription(tagTypeOptions, tag.Type), tag.Value]
                .filter(Boolean)
                .join(': ');

              return (
                <RMSIconButton
                  key={tag.ptsTagID}
                  className="pd-header__tag-icon-button"
                  icon={<TagIcon className="pd-header__tag-icon" />}
                  label={description}
                  tooltip={description}
                  onClick={() => onMenuSelect?.(PersonMenuItemKey.TAGS)}
                />
              );
            })}
          </div>
          <RMSButton
            className="pd-header__tags-button"
            label="Add Tag"
            prefixIcon={<AddIcon fontSize="small" />}
            variant="outlined"
            size="small"
            onClick={onAddTagClick}
          />
        </Box>
      </Box>

      <Box className="pd-header__actions pd-card">
        <Typography className="pd-header__actions-title">Actions</Typography>
        <Box className="pd-header__actions-grid">
          <Box className="pd-header__action">
            <RMSIconButton
              className="pd-header__action-icon"
              label="Print History"
              tooltip="Print History"
              icon={<PrintIcon />}
              color="primary"
              disabled={!personId}
              onClick={printPersonHistory}
            />
            <small>Print History</small>
          </Box>
          <Box className="pd-header__action">
            <RMSIconButton
              className="pd-header__action-icon"
              label="Merge Person"
              tooltip="Merge Person"
              icon={<MergeTypeIcon />}
              color="primary"
              disabled={!personId || !canMergePerson || !person || Boolean(person.IsDeleted)}
              onClick={() => setMergePerson({ ...person, id: personId, ptsPersonID: personId })}
            />
            <small>Merge Person</small>
          </Box>
        </Box>
      </Box>
      {mergePerson && (
        <PersonMerge
          showTargetPerson
          rows={[mergePerson]}
          targetID={mergePerson.id}
          columns={mergeColumns}
          close={() => {
            setMergePerson(null);
            setMergeRefreshKey((key) => key + 1);
          }}
        />
      )}
    </Box>
  );
};

export default PersonHeader;
